import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injector, untracked } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  APIKeysControllerService,
  ApiKeyDto,
  LoginControllerService,
  LoginRequest,
  User2FAControllerService,
  UserChangePasswordRequest,
  UserControllerService,
} from '@api/client';
import { GoogleIdentityService, requiresPasswordChange } from '@stillmandigital/common';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { SharedStore } from '@shared/store/shared.signal-store';
import { AuthService } from 'app/auth/shared/services/auth.service';
import { finalize, mergeMap, pipe, switchMap } from 'rxjs';
import { toErrorTranslationKey } from 'app/auth/components/login-page/login-page.errors';
import { AuthState } from './auth.interfaces';
import { mapGoogleSsoError, readEmailFromCredential } from './google-sso-errors';

const WRONG_OLD_PASSWORD_MESSAGE = 'Wrong password';

const initialState: AuthState = {
  secretKey: undefined,
  loginError: undefined,
  apiKeys: [],
  twoFaDialogShown: false,
  ssoStep: 'credentials',
  ssoEmail: undefined,
  ssoPending: false,
  ssoCredential: undefined,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthState>(initialState),
  withProps(() => ({
    _sharedStore: inject(SharedStore),
    _authService: inject(AuthService),
    _router: inject(Router),
    _injector: inject(Injector),
    _dialogService: inject(MatDialog),
    _notificationService: inject(NotificationService),
    _loginService: inject(LoginControllerService),
    _usersService: inject(UserControllerService),
    _user2FAService: inject(User2FAControllerService),
    _apiKeyService: inject(APIKeysControllerService),
    _googleIdentity: inject(GoogleIdentityService),
  })),
  withComputed(store => {
    const twoFALink = computed(() => {
      const user = store._sharedStore.currentUser();
      const secretKey = store.secretKey();

      return `otpauth://totp/${user?.login}?secret=${secretKey}&algorithm=SHA1&digits=6&period=30&issuer=Stillman%20Digital`;
    });

    return { twoFALink };
  }),
  withMethods(store => {
    const clearLoginError = () => patchState(store, { loginError: undefined });

    /**
     * Every path that ends the session must also drop Google's auto-select, or the
     * next visit silently re-signs the same account in.
     */
    const _logOut = () => {
      store._googleIdentity.disableAutoSelect();
      store._authService.logOut();
    };

    const googleLogin = rxMethod<{ credential: string; twoFACode?: string }>(
      pipe(
        switchMap(({ credential, twoFACode }) => {
          patchState(store, {
            ssoPending: true,
            loginError: undefined,
            ssoCredential: credential,
            ssoEmail: readEmailFromCredential(credential) ?? undefined,
          });

          return store._loginService.loginWithGoogle({ credential, twoFACode }).pipe(
            tapResponse({
              next: ({ token }) => {
                patchState(store, { ssoPending: false });
                store._authService.setToken(token || '');
                store._authService.navigateToReturnUrl();
              },
              error: (error: HttpErrorResponse) => {
                // The backend signals "needs a 2FA code" as an error; only some codes
                // are real failures, and only some let us keep the Google credential.
                const { nextStep, messageKey, keepCredential } = mapGoogleSsoError(error);

                patchState(store, state => ({
                  ssoPending: false,
                  ssoStep: nextStep,
                  loginError: messageKey ?? undefined,
                  ssoCredential: keepCredential ? state.ssoCredential : undefined,
                  ssoEmail: keepCredential ? state.ssoEmail : undefined,
                }));
              },
            }),
          );
        }),
      ),
    );

    const cancelGoogleLogin = () => {
      store._googleIdentity.disableAutoSelect();
      patchState(store, {
        ssoStep: 'credentials',
        ssoPending: false,
        ssoCredential: undefined,
        ssoEmail: undefined,
        loginError: undefined,
      });
    };

    const login = rxMethod<LoginRequest & { captchaToken?: string }>(
      pipe(
        switchMap(request =>
          store._loginService.login(request).pipe(
            tapResponse({
              next: ({ token }) => {
                store._authService.setToken(token || '');
                store._authService.navigateToReturnUrl();
              },
              error: (error: HttpErrorResponse) =>
                patchState(store, { loginError: toErrorTranslationKey(error) }),
            }),
          ),
        ),
      ),
    );

    const logout = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._loginService
            .logout({ sessionToken: store._authService.getToken() })
            .pipe(
              tapResponse({
                next: () => _logOut(),
                error: () => undefined,
              }),
            ),
        ),
      ),
    );

    const changePassword = rxMethod<UserChangePasswordRequest>(
      pipe(
        switchMap(request =>
          store._usersService.changePassword(request).pipe(
            tapResponse({
              next: () => {
                store._notificationService.showSuccessNotification(
                  'NOTIFICATION.THE_PASSWORD_HAS_BEEN_CHANGED_SUCCESSFULLY',
                );
                _logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(
                  error.error?.message === WRONG_OLD_PASSWORD_MESSAGE
                    ? 'NOTIFICATION.THE_OLD_PASSWORD_IS_WRONG'
                    : 'NOTIFICATION.THE_PASSWORD_COULDNT_BE_CHANGED',
                  error,
                ),
            }),
          ),
        ),
      ),
    );

    const enable2FA = rxMethod<string>(
      pipe(
        switchMap(twoFACode =>
          store._user2FAService.enable2FASecurity({ twoFACode }).pipe(
            tapResponse({
              next: () => {
                store._notificationService.showSuccessNotification(
                  'NOTIFICATION.THE_2FA_HAS_BEEN_ENABLED_SUCCESSFULLY',
                );
                _logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(
                  'NOTIFICATION.THE_2FA_COULDNT_BE_ENABLED',
                  error,
                ),
            }),
          ),
        ),
      ),
    );

    const disable2FA = rxMethod<string>(
      pipe(
        switchMap(twoFACode =>
          store._user2FAService.disable2FASecurity({ twoFACode }).pipe(
            tapResponse({
              next: () => {
                store._notificationService.showSuccessNotification(
                  'NOTIFICATION.THE_2FA_HAS_BEEN_DISABLED_SUCCESSFULLY',
                );
                _logOut();
              },
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(
                  'NOTIFICATION.THE_2FA_COULDNT_BE_DISABLED',
                  error,
                ),
            }),
          ),
        ),
      ),
    );

    const load2FALink = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._user2FAService.get2FASecurity().pipe(
            tapResponse({
              next: ({ secretKey }) => patchState(store, { secretKey }),
              error: () => undefined,
            }),
          ),
        ),
      ),
    );

    const loadApiKeys = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._apiKeyService.getApiKeys(store._sharedStore.activeAccountId()).pipe(
            tapResponse({
              next: (apiKeys: ApiKeyDto[]) => patchState(store, { apiKeys }),
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(
                  'NOTIFICATION.API_KEYS_COULDNT_BE_LOADED',
                  error,
                ),
            }),
          ),
        ),
      ),
    );

    const invalidateApiKey = rxMethod<number>(
      pipe(
        mergeMap(keyId =>
          store._apiKeyService
            .invalidateApiKey({ keyId, accountId: store._sharedStore.activeAccountId() })
            .pipe(
              tapResponse({
                next: () =>
                  store._notificationService.showSuccessNotification(
                    'NOTIFICATION.API_KEY_INVALIDATED',
                  ),
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(
                    'NOTIFICATION.API_KEY_NOT_INVALIDATED',
                    error,
                  ),
              }),
              finalize(() => loadApiKeys()),
            ),
        ),
      ),
    );

    const openChangePasswordDialog = async () => {
      const { ChangePasswordDialogComponent } =
        await import('app/auth/components/change-password-dialog/change-password-dialog.component');

      store._dialogService.open(ChangePasswordDialogComponent, {
        injector: store._injector,
      });
    };

    const openApiKeyDialog = async () => {
      const { ApiKeyDialogComponent } =
        await import('app/auth/components/api-key-dialog/api-key-dialog.component');

      store._dialogService.open(ApiKeyDialogComponent, {
        autoFocus: false,
        injector: store._injector,
      });
    };

    const openEnableTwoFactorAuthDialog = async (disableClose: boolean) => {
      const { EnableTwoFactorAuthDialogComponent } =
        await import('app/auth/components/enable-two-factor-auth-dialog/enable-two-factor-auth-dialog.component');

      store._dialogService.open(EnableTwoFactorAuthDialogComponent, {
        disableClose,
        injector: store._injector,
      });
    };

    return {
      clearLoginError,
      login,
      googleLogin,
      cancelGoogleLogin,
      logout,
      changePassword,
      enable2FA,
      disable2FA,
      load2FALink,
      loadApiKeys,
      invalidateApiKey,
      openChangePasswordDialog,
      openApiKeyDialog,
      openEnableTwoFactorAuthDialog,
    };
  }),
  withHooks(store => ({
    onInit() {
      effect(() => {
        const user = store._sharedStore.currentUser();

        if (!user) {
          return;
        }

        // shellAccessGuard sends this user to change their password; 2FA setup waits until then.
        if (requiresPasswordChange(user)) {
          return;
        }

        if (!user.enabled2FA && !untracked(() => store.twoFaDialogShown())) {
          untracked(() => {
            patchState(store, { twoFaDialogShown: true });
            store.openEnableTwoFactorAuthDialog(true);
          });
        }
      });
    },
  })),
);
